import React, { Suspense } from 'react'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout } from '@fs/zion-ui'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import type { BrowserOptions } from '@sentry/browser'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { NoticeLoading } from '@fs/zion-icon'
import App from './App'
import { PlacesAppProvider } from './components/PlacesApp/PlacesAppContext'
import * as serviceWorker from './serviceWorker'
import applyFlagLinkOverrides from './flagLinkOverrides'
import './locales'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

type SentryConfig = Partial<BrowserOptions>

const treeSentryDefaults: SentryConfig = {
  ignoreErrors: [
    /^.*-extension:\/\/.*$/,
    /^.*AbortError: The transaction was aborted.*$/,
    /^.*AbortError: Version change transaction was aborted.*$/,
    /^.*Blocked a frame with origin.*$/,
    /^.*Can't find variable.*$/,
    /^.*Connection is closing.*$/,
    /^.*Connection on cached page closed.*$/,
    /^.*connection was closed.*$/,
    /^.*ChunkLoadError.*$/,
    /^.*Database deleted.*$/,
    /^.*database is a higher version.*$/,
    /^.*database using a lower version.*$/,
    /^.*disk is full.*$/,
    /^.*Error creating or migrating .* in database.*$/,
    /^.*Error looking up record in object store.*$/,
    /^.*Error:.*Indexed Database server.*$/,
    /^.*Extension context invalidated.*$/,
    /^.*Failed to execute 'replaceState' on 'History'.*$/,
    /^.*flatMap.*$/,
    /^.*Internal error retrieving bucket.*$/,
    /^.*is not defined.*$/,
    /^.*Java object is gone.*$/,
    /^.*Loading CSS chunk.*$/,
    /^.*Munchkin.*$/,
    /^.*Network Error.*$/,
    /^.*NetworkError when attempting to fetch resource.*$/,
    /^.*No Session Id.*$/,
    /^.*no user signed in.*$/,
    /^.*Non-Error promise rejection captured.*$/,
    /^.*One of the specified object stores was not found.*$/,
    /^.*QuotaExceededError.*$/,
    /^.*redefine .*property.*$/,
    /^.*Request aborted.*$/,
    /^.*ResizeObserver loop limit exceeded.*$/,
    /^.*SecurityError: The operation is insecure.*$/,
    /^.*status.*401.*$/,
    /^.*status.*403.*$/,
    /^.*status.*404.*$/,
    /^.*status.*410.*$/,
    /^.*status.*422.*$/,
    /^.*The operation failed for reasons unrelated to the database itself.*$/,
    /^.*The requested version.*$/,
    /^.*The user denied permission to access the database.*$/,
    /^.*Transaction timed out due to inactivity.*$/,
    /^.*Unable to open database.*$/,
    /^.*DataError: Failed to read large IndexedDB value.*$/,
    /^.*window\.onunload is not a function.*$/,
    /^.*is not a child of this node.*$/,
    /^.*Attempt to get a record from database without an in-progress transaction.*$/,
    /^.*Network Error.*(dynatrace|dtAWF).*$/,
    /^.*AxiosError.*dtAWF.*$/,
    /^.*AxiosError.*dynatrace.*$/,
    /^.*AxiosError.*ruxitagent.*$/,
    /^.*AxiosError.*jstag.*$/,
  ],
  denyUrls: [
    /edge\.fscdn\.org\/assets\/static\/css\//,
    /js-cdn\.dynatrace\.com\/jstag\//,
    /js-cdn\.dynatrace\.com/,
    /ruxitagent/,
    /dtAWF/,
  ],
  allowUrls: [/edge\.fscdn\.org/, /familysearchcdn\.org/, /https?:\/\/((www|beta|integration)\.)?familysearch\.org/],
}

if (sentryDSN) {
  Sentry.init(
    mergeSentryConfig({
      ...treeSentryDefaults,
      dsn: sentryDSN,
      ignoreErrors: [
        "Must wait for 'idle' event on map before calling markersNearAnyOtherMarker",
        /RequestAnimationFrame/,
        /status.*401 /,
        /-extension:\/\//,
        /Blocked a frame with origin/,
        /ChunkLoadError/,
        /Extension context invalidated/,
        /no user signed in/,
        /QuotaExceededError/,
        /StorageQuotaExceeded/,
        /redefine .* property/,
        /ResizeObserver loop limit exceeded/,
        /status.*401/,
        /status.*404/,
        /status.*410/,
        /Non-Error promise rejection captured with keys:/,
        /utag is not defined/,
        /flatMap is not a function/,
      ],
      sampleRate: 0.75,
    })
  )
}

const base: string = appPath ? new URL(appPath).pathname : ''

const FrontierWrapper = (): JSX.Element => {
  setAppLocales(essentialLocales)

  return (
    <PlacesAppProvider>
      <Router basename={base}>
        <Root skipLinkId="resultsWrapper" analytics footer={{ hidden: true }} helpChatbot={false}>
          <AppLayout fullWidth>
            <App />
          </AppLayout>
        </Root>
      </Router>
    </PlacesAppProvider>
  )
}

const FrontierRoot = (): JSX.Element => (
  <React.StrictMode>
    <Suspense fallback={<NoticeLoading />}>
      <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
        <FrontierWrapper />
      </FrontierBaseProviders>
    </Suspense>
  </React.StrictMode>
)

applyFlagLinkOverrides()

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

serviceWorker.unregister()
